import { Form } from '../forms/form';
import { DepositfixProvider } from '../providers/depositfix-provider';
import { WidgetView } from '../view/widget-view';
import { PaymentType } from '../providers/payment-type';
import { Config } from '../config';
import { FormTypes } from '../forms/form-types';
import { defaultConfig } from '../config/default-config';
import { DiscountForm } from '../forms/discount-form';
import { StripeForm } from '../forms/stripe-form';
import { Subtotal } from '../config/subtotal';
import { Discount } from '../config/discount';
import { OrderItem } from '../config/order-item';
import { Order } from '../config/order';
import { HubSpotForm } from '../forms/hubspot-form';
import { MultipleContactsForm } from '../forms/multiple-contacts-form';
import { ProductForm } from '../forms/product-form';
import { SubscriptionForm } from '../forms/subscription-form';
import { AddonForm } from '../forms/addon-form';
import { ProductType } from '../config/product-type';
import { DonationFrequencyType } from '../config/donation-frequency-type';
import { DonationForm } from '../forms/donation-form';
import { Translations } from '../config/translations';
import { ErrorView } from '../view/error-view';
import { ErrorType } from '../config/error-type';
import { StripeGooglePayForm } from '../forms/stripe-google-pay-form';
import { Util } from '../config/utils';
import { StripeApplePayForm } from '../forms/stripe-apple-pay-form';
import { StripeMobilePayForm } from '../forms/stripe-mobile-pay-form';
import { RecaptchaV3Form } from '../forms/recaptcha-v3-form';
import { RecaptchaV2Form } from '../forms/recaptcha-v2-form';
import { OfflinePaymentForm } from '../forms/offline-payment-form';
import { LogFailingOnInitForm } from '../forms/z-log-failing-on-init';
import { LogFailingOnRenderForm } from '../forms/z-log-failing-on-render';
import { StripeAchForm } from '../forms/stripe-ach-form';
import { FormsFactory } from '../forms/forms-factory';
import { Product } from '../config/product';
import { UpSellForm } from '../forms/upsell/upsell-form';
import { BehaviorSubject, Subject } from 'rxjs';
import { IUpSellEvent } from '../config/upsell-response';
import { PaypalForm } from '../forms/paypal-form';
import { CountdownForm } from '../forms/countdown-form';
import { StripeBacsForm } from '../forms/stripe-bacs-form';
import { GoogleAnalyticsForm } from '../forms/google-analytics-form';
import {HubspotProvider} from '../providers/hubspot-provider';

declare const process: any;

export class Widget extends Form {

    private isLogging = process.env.ISLOGGING;

    view: WidgetView = new WidgetView();
    errorView: ErrorView = new ErrorView();
    totalAmount = 0;
    testMode = 'live';
    paymentMethod: PaymentType = PaymentType.STRIPE;
    private readonly provider: DepositfixProvider;

    private readonly hsProvider: HubspotProvider;
    discountType: any;
    dynamicPlanInterval: any;
    subscriptionPlanId: any;
    discountId: any;
    amountDiscounted = 0;
    noPayment = false;
    data: any = {};

    hsThankYouMessage = '';
    hsInputRedirectUrl = '';

    stepSubject: BehaviorSubject<number> = new BehaviorSubject(1);
    lastStepValue = 1;

    initCallbacks = {
        amountChange: () => this.triggerAmount(),
        hidePayments: () => this.hidePaymentForms(),
        showPayments: () => this.showPaymentForms(),
        getTotalAmount: () => this.sendTotalAmount.bind(this)(),
        coverFeesChange: () => this.coverFeesChange(),
        resetDiscount: () => this.resetDiscount()
    };

    constructor(public forms: Form[]) {
        super();
        this.provider = new DepositfixProvider();
        this.hsProvider = new HubspotProvider();
    }

    public triggerAppEvent(methodName: any, data?: any) {
        const formCallbackResult = this.invokeFormCallback(methodName, data);
        const inlineCallbackResult = this.invokeInlineCallback(methodName, data);
        return {
            formCallbackResult: formCallbackResult,
            inlineCallbackResult: inlineCallbackResult
        };
    }

    private invokeFormCallback(methodName: any, data?: any) {
        if (this.config.callbacks && this.config.callbacks.formCallbacks[methodName]) {
            try {
                return this.config.callbacks.formCallbacks[methodName](data);
            } catch (e: any) {
                this.logger.log(`Callback error - [${methodName}]: ${e}`);
                return {};
            }
        }
    }

    private invokeInlineCallback(methodName: any, data?: any) {
        if (this.config.callbacks && this.config.callbacks.inlineCallbacks[methodName]) {
            try {
                return this.config.callbacks.inlineCallbacks[methodName](data);
            } catch (e: any) {
                this.logger.log(`Inline callback error - [${methodName}]: ${e}`);
                return {};
            }
        }
    }

    async render() {
        this.view.renderWidgetContainer();

        const isNoPayment = false;

        //  load theme css
        if (this.config.settings.cssFile !== false && this.config.settings.cssFile !== 'false') {
            this.view.loadThemeCss(this.config.settings.cssFile || this.getCssFile());
        }

        for (let i = 0; i < this.forms.length; i++) {
            const form = this.forms[i];
            form.isNoPayment = isNoPayment;
            this.isLogging
                ? await form.render()
                    .catch((err: Error) => {
                        err.message = 'Form ' + form.type + ' had an error while rendering: ' + err.message;
                        this.provider.logError(err, { formId: this.config.formId, portalId: this.config.user.portalId });
                    })
                : await form.render();
        }

        setTimeout(() => {

            const hubSpotForm = this.getHubSpotForm();
            this.view.applyCss(hubSpotForm.hubSpotFormDOM);
            if (hubSpotForm.doesCaptchaExist()) {
                this.errorView.render(ErrorType.CAPTCHA_EXISTS);
            }

            // CUSTOM CSS FROM FORM EDITOR
            this.renderCssFormStyle();
            // END OF CSS FROM FORM EDITOR

            const cssStyles = this.config.settings.cssStyles;
            if (cssStyles !== undefined && cssStyles !== null) {
                this.view.loadCssCode(this.config.settings.cssStyles);
            }
        }, 300);

        const payButtonText = this.config.settings.payButtonText;
        if (payButtonText !== null && payButtonText !== undefined && payButtonText.trim().length > 1) {
            Translations.PAY_NOW = payButtonText;
            Translations.OFFLINE_PAYMENT = payButtonText;
        }
        this.view.renderWidgetFooter(this.submit.bind(this));

        const stripeMobilePayForm: StripeMobilePayForm = this.getStripeMobilePayForm();
        if (stripeMobilePayForm) {
            stripeMobilePayForm.createPaymentRequest();
        }

        const stripeAchForm: StripeAchForm = this.getStripeAchForm();
        if (stripeAchForm) {
            stripeAchForm.renderForm();
        }

        const [paymentMethod] = this.config.paymentMethod;
        this.paymentMethod = paymentMethod as PaymentType;
        if (this.config.paymentMethod.length > 1) {
            this.triggerAmount();
            this.view.renderPaymentTypeRadioButtons(
                this.config.paymentMethod,
                this.config.forms.hubspot.id,
                this.onPaymentTypeChange.bind(this)
            );
            this.changePaymentSystem(paymentMethod as string);
        } else {
            if (this.paymentMethod === PaymentType.PAYPAL) {
                this.view.hidePayNowButton();
            }
            this.triggerAmount();
        }
        this.changePaymentSystem(this.paymentMethod);

        if (this.isCoverFeesEnabed()) {
            this.renderCoverFees();
        }

        const urlAmount = Util.getUrlParameter('amount', window.location.href);
        if (urlAmount && this.config.products.length === 1) {
            this.setAmountFromUrl(urlAmount);
        }

        const dealId = Util.getUrlParameter('dfd', window.location.href);
        if (dealId) {
            this.config.dealId = dealId;
        }

        this.initSteps();
        this.renderTestBanner();

        Util.getCountryCode().then((res: any) => {
            this.config.session = res;
            this.provider.sendPageEvent(this.config);
        }).catch(() => this.provider.sendPageEvent(this.config));
    }

    getCssFile() {
        return Util.isDfScriptV2() ? process.env.CSS_FILE_V2 : process.env.CSS_FILE;
    }

    initSteps() {
        if (this.forms.find(form => Boolean(form.step))) {
            const maxStep = Math.max(...this.forms.map(form => form.step || 0));
            this.view.renderStepButtons(this.stepSubject, maxStep, this);

            if (this.config.settings.paymentStep) {
                this.view.hidePaymentMethods();
                this.view.hideFooterForm();
            }
            this.addStepListener();
        }
    }

    addStepListener() {
        this.stepSubject.subscribe(step => {
            const allStepForms = this.forms.filter(form => Boolean(form.step));
            const nextStepForms = allStepForms.filter(form => form.step === step);

            allStepForms.forEach(form => form.hideForm());
            nextStepForms.forEach(form => form.showForm());

            if (this.config.settings.paymentStep === step) {
                this.changePaymentSystem(this.paymentMethod);
                this.view.showPaymentMethods();
                this.view.showFooterForm();
            } else {
                this.view.hidePaymentMethods();
                this.view.hideFooterForm();
            }
        });
    }

    watchSteps(nextStep: number) {
        if (nextStep < this.lastStepValue) {
            this.stepSubject.next(nextStep);
            return;
        }
        const currentStepForms = this.forms.filter(form => form.step === this.lastStepValue);
        Promise
            .all(currentStepForms.map(form => form.validate()))
            .then(validationResults => {
                if (validationResults.every(isValid => isValid)) {
                    this.stepSubject.next(nextStep);
                }
            });
    }

    renderTestBanner() {
        if (this.isTestMode()) {
            this.testMode = 'test';
            this.view.renderTestModeBanner();
        }
    }

    renderCssFormStyle() {
        if (this.config.settings.cssFormStyle && this.config.settings.cssFormStyle.length) {
            this.insertCssFormStyle(this.config.settings.cssFormStyle);
        }
    }

    insertCssFormStyle(cssFormStyle: string) {
        if (document.head) {
            const existingDfpProtoFormStyleContainer = document.getElementById('dpf-proto-form-style');
            if (existingDfpProtoFormStyleContainer) {
                document.head.removeChild(existingDfpProtoFormStyleContainer);
            }
            const dfpProtoFormStyleContainer = document.createElement('style');
            dfpProtoFormStyleContainer.setAttribute('id', 'dpf-proto-form-style');
            dfpProtoFormStyleContainer.setAttribute('type', 'text/css');
            dfpProtoFormStyleContainer.textContent = cssFormStyle;
            document.head.appendChild(dfpProtoFormStyleContainer);
        }
    }

    isTestMode = () => {
        return Object.keys(this.config.forms)
            .some(key => {
                const form = this.config.forms[key];
                return form.mode === 'test';
            });
    }

    onPaymentTypeChange(event: any) {
        const value = event.target.value;
        this.changePaymentSystem(value);
    }

    changePaymentSystem(value: string) {
        this.paymentMethod = value as PaymentType;
        this.config.currentPaymentMethod = this.paymentMethod;
        this.forms.forEach((form: Form) => {
            if (this.config.paymentMethod.includes(form.type)) {
                form.active = false;
            }
        });
        const selectedForm = this.forms.find(form => form.type === value);
        if (selectedForm) {
            selectedForm.active = true;
        }

        this.triggerAmount();
    }

    isCoverFeesEnabed() {
        const coverFees = this.config.settings.coverFees;
        return coverFees !== null && coverFees !== undefined && coverFees === true;
    }

    renderCoverFees() {
        this.view.renderCoverFees(this.coverFeesChange.bind(this));
    }

    setAmountFromUrl(urlAmount: string) {
        this.forms.forEach((form: Form) => form.setAmountFromUrl(urlAmount));
    }

    triggerAmount() {
        let isNoPayment = false;
        let atLeastOneNoPayment = false;
        let totalOriginalAmount = 0;
        this.totalAmount = 0;

        const subtotal = new Subtotal();
        const selectedSubs: any = [];

        this.forms.forEach((form: Form) => {
            if (form.paymentData.amount > 0) {
                switch (form.type) {
                    case FormTypes.Product:
                        if (form.quantity && form.quantity > 1) {
                            subtotal.quantity = form.quantity;
                        }
                        subtotal.singleAmount += form.paymentData.amount;
                        subtotal.singleTotal = subtotal.singleAmount;
                        break;
                    case FormTypes.Subscription:
                        selectedSubs.push(form.getSelectedProducts());
                        subtotal.subscriptionAmount = form.paymentData.amount;
                        subtotal.subscriptionTotal = subtotal.subscriptionAmount;
                        break;
                    case FormTypes.Addon:
                        selectedSubs.push(form.getSelectedProducts());
                        subtotal.singleAmount += form.paymentData.singlePaymentAmount;
                        subtotal.singleTotal = subtotal.singleAmount;
                        subtotal.subscriptionAmount += form.paymentData.subscriptionAmount;
                        subtotal.subscriptionTotal = subtotal.subscriptionAmount;
                        break;
                    default:
                        subtotal.singleAmount += form.paymentData.amount;
                        subtotal.singleTotal = subtotal.singleAmount;
                        break;
                }

                this.totalAmount += form.paymentData.amount;
            }

            if (form.paymentData.type === ProductType.NO_PAYMENT) {
                atLeastOneNoPayment = true;
            }

            if (form.type === FormTypes.Addon) {
                const addonForm = this.getAddonForm();

                if (addonForm.getSelectedProducts().find((product: any) => product.type === ProductType.NO_PAYMENT)) {
                    atLeastOneNoPayment = true;
                }
            }
        });

        const checkDiscountForm = this.forms.find(x => x.type === FormTypes.Discount);
        if (checkDiscountForm) {
            (checkDiscountForm as DiscountForm).selectedSubs = selectedSubs.flat().filter((prod: any) => prod.type === 'subscription');
        }

        this.applySubtotalDiscounts(subtotal);

        totalOriginalAmount = subtotal.subscriptionAmount + subtotal.singleAmount;
        this.totalAmount = subtotal.subscriptionTotal + subtotal.singleTotal;

        //total amount was cleared by discount
        if (this.totalAmount === 0 && (totalOriginalAmount !== 0 || atLeastOneNoPayment)) {
            isNoPayment = true;
        }

        if (this.config.settings.tax) {
            subtotal.taxRate = this.config.settings.tax;
            const tax = this.setTax(this.totalAmount);
            subtotal.tax = tax;
            this.data.taxAmount = tax;
        }

        if (this.totalAmount <= 0 && isNoPayment) {
            this.hidePaymentForms();
        } else {
            if (this.config.settings.paymentStep) {
                if (this.config.settings.paymentStep === this.stepSubject.value) {
                    this.showPaymentForms();
                }
            } else {
                this.showPaymentForms();
            }
        }

        this.view.setChangedAmount(subtotal, this.config.settings.currency, `${this.totalAmount}`, this.forms);

        this.updateSubscriptionIntervalText();

        const stripeMobilePayForm: StripeMobilePayForm = this.getStripeMobilePayForm();
        if (stripeMobilePayForm) {
            this.updatePaymentRequest(stripeMobilePayForm);
        }
    }

    updateSubscriptionIntervalText = () => {
        const subscriptionForm: SubscriptionForm = this.getSubscriptionForm();
        if (subscriptionForm) {
            const product: any = subscriptionForm.getSelectedProducts()[0];
            const interval = product.interval;
            const maxCharges = product.maxCharges;
            if (this.areProductTypesDifferent() || subscriptionForm.quantity > 1) {
                this.view.setSubscriptionSubtotalInterval(interval, maxCharges);
            } else {
                this.view.setSubscriptionInterval(interval, maxCharges);
            }
            return;
        }
        const donationForm: DonationForm = this.getDonationForm();
        if (donationForm) {
            this.view.setSubscriptionInterval(donationForm.view.getSubscriptionPlan());
        }

    }

    areProductTypesDifferent = (): boolean => {
        const products = this.config.products;
        if (products.length === 1) {
            return false;
        }
        const productTypes: string[] = [];
        for (let i = 0; i < products.length; i++) {
            const type = products[i].type;
            if (type !== ProductType.NO_PAYMENT && productTypes.indexOf(type) === -1) {
                productTypes.push(type);
                if (productTypes.length > 1) {
                    return true;
                }
            }
        }
        return false;
    }

    updatePaymentRequest(stripeMobilePayForm: StripeMobilePayForm) {
        const selectedProducts = this.getAllSelectedProducts();
        const label = selectedProducts.map((selectedProduct: any) => selectedProduct.name).join(', ');
        stripeMobilePayForm.updatePaymentRequest(label, this.totalAmount);
    }

    getAllSelectedProducts() {
        const allSelectedProducts: any[] = [];
        this.forms.forEach((form: Form) => {
            const selectedProducts = form.getSelectedProducts();
            if (selectedProducts) {
                selectedProducts.forEach((selectedProduct: any) => {
                    allSelectedProducts.push(selectedProduct);
                });
            }
        });
        return allSelectedProducts;
    }

    setExternalAmount(value: number) {
        this.config.externalAmount = value;
    }

    applySubtotalDiscounts(subtotal: Subtotal) {
        const discountForm = this.getDiscountForm();

        if (discountForm) {
            for (const discount of discountForm.discounts) {

                if (discount.type === 'discountUrl') {
                    const discountAmount = this.calcDiscount(subtotal.singleAmount, discount);

                    subtotal.singleDiscount = discountAmount;
                    subtotal.singleTotal = Math.max(subtotal.singleAmount - discountAmount, 0);
                } else if (discount.type === 'couponUrl') {
                    const discountAmount = this.calcDiscount(subtotal.subscriptionAmount, discount);

                    subtotal.subscriptionDiscount = discountAmount;
                    subtotal.subscriptionTotal = Math.max(subtotal.subscriptionAmount - discountAmount, 0);
                }
            }
        }
    }

    calcDiscount(amount: number, discount: Discount) {

        let discountAmount = 0;

        if (discount.amount) {
            discountAmount = discount.amount;
        } else if (discount.percentOff) {
            const discountPercent = discount.percentOff;
            discountAmount = this.toTwoDec(amount * (discountPercent / 100));
        }

        return discountAmount;
    }

    getTax(amount: number) {
        if (this.config.settings.tax <= 0) {
            return 0;
        }

        return this.toTwoDec(amount / 100 * this.config.settings.tax);
    }

    setTax(amount: number) {
        const tax = +this.getTax(amount);
        this.totalAmount = +amount + tax;
        return tax;
    }

    hidePaymentForms() {

        this.noPayment = true;

        this.forms.forEach((form) => {
            if (this.config.paymentMethod.includes(form.type)) {
                form.hidePaymentFields();
            }
        });
    }

    showPaymentForms() {

        this.noPayment = false;

        this.forms.forEach((form) => {
            if (this.paymentMethod) {
                if (form.type as string === this.paymentMethod as string) {
                    form.showPaymentFields();
                }
            }
        });
    }

    resetDiscount() {
        const discountForm = this.getDiscountForm();
        if (discountForm) {
            discountForm.resetDiscount();
        }
    }

    sendTotalAmount() {
        return this.totalAmount;
    }

    coverFeesChange() {
        const donationForm = this.getDonationForm();
        if (donationForm) {
            donationForm.calculateCoverFees();
        }
    }

    loadJS(src: string) {
        this.view.loadJS(src);
    }

    async init(config: Config) {
        super.init(config, {});

        this.config.dfProvider = this.provider;

        config.widget = this;

        await Promise.all(this.forms.map(form => this.isLogging
            ? form.init(config, this.initCallbacks).catch(err => {
                err.message = `An error occured while initializing from - ${form.type} -: ` + err.stack;
                this.provider.logError(err, { formId: this.config.formId, portalId: this.config.user.portalId });
            })
            : form.init(config, this.initCallbacks)
        ));
    }

    validate() {
        let valid = true;

        const paymentForm: any = this.forms.find(form => (form.type === 'stripe' || form.type === 'offline_payment') && form.active);
        const isNoPayment: boolean = this.forms.some(form => form.isNoPayment);


        const promises = this.forms.map(form => form.validate());
        return Promise.all(promises).then(results => results.every(x => valid = valid && x)).then(res => {
            if (paymentForm && valid && paymentForm.isNoTotal) {
                paymentForm.active = false;
            }

            if (valid && isNoPayment && paymentForm) {
                paymentForm.active = false;
            }

            return valid;
        });

        // get flag from products and subscriptions to deactivate payment form
        // const types = [FormTypes.Products, FormTypes.Subscription];
        // types.forEach((type: string) => {
        //     this.forms.forEach((form: any) => {
        //         if (valid && paymentForm && (form.type === type) && form.hideCreditCard) {
        //             paymentForm.active = false;
        //         }
        //     });
        // });


        //return valid;
    }

    redirectOrMessage() {
        if (this.getThankYouMessage()) {
            this.view.renderThankYouMessage(this.getThankYouMessage());
        } else {
            this.redirectToUrl(this.getRedirectUrl());
        }
    }

    externalRedirect() {
        this.redirectToUrl(this.getRedirectUrl());
    }

    getRedirectUrl() {
        return this.config.settings.redirectUrl || this.getHubSpotRedirectUrl() || defaultConfig.fallbackRedirectUrl;
    }

    getThankYouMessage() {
        return this.getHubSpotForm().getThankYouMessage();
    }

    getHubSpotRedirectUrl() {
        return this.getHubSpotForm()?.getRedirectUrl();
    }

    redirectToUrl(url) {
        if (Util.isDfFormIFrame() && window.top) {
            window.top.location = url;
        } else {
            window.location = url;
        }
    }

    private createOrderItems(products: Array<any>, discounts: Array<Discount>): Array<OrderItem> {

        const dynamicPlanInterval = this.dynamicPlanInterval;
        const orderItems = [] as OrderItem[];

        let singleDiscount: any = {};
        let subscriptionDiscount: any = {};
        let subscriptionTrialDaysDiscount: any = {};

        if (discounts.length > 0) {
            singleDiscount = (discounts.find(d => d.type === 'discountUrl') || { id: '' });
            subscriptionDiscount = (discounts.find((d: any) => d.type === 'couponUrl' && !d.trialDays) || { id: '' });
            subscriptionTrialDaysDiscount = (discounts.find((d: any) => d.type === 'couponUrl' && d.trialDays) || { id: '' });
        }

        let isFirstSubscriptionDiscountApplied = false;

        for (const product of products) {

            const orderItem = {} as OrderItem;

            orderItem.dfProductId = product.dfProductId;
            orderItem.amount = +product.amount;
            orderItem.currency = this.config.settings.currency;
            orderItem.subscriptionPlanId = product.subscriptionPlanId || null;

            if (this.config.settings.tax !== undefined) {
                orderItem.tax = this.config.settings.tax;
            }

            orderItem.productName = product.name;

            if (dynamicPlanInterval) {
                orderItem.dynamicPlanInterval = dynamicPlanInterval;
            }

            if (product.type) {
                orderItem.paymentType = product.type.toUpperCase();
            }

            if (product.quantity) {
                orderItem.quantity = product.quantity;
            }

            if (product.type === ProductType.SINGLE_PAYMENT) {
                orderItem.discountId = singleDiscount.id;
            } else if (product.type === ProductType.SUBSCRIPTION) {
                if (!isFirstSubscriptionDiscountApplied && subscriptionDiscount.id) {
                    orderItem.couponId = subscriptionDiscount.id;
                    const orderItemAmount = orderItem.amount + (this.config.settings.tax !== undefined ? this.getTax(orderItem.amount) : 0);
                    if (subscriptionDiscount.amountOff >= orderItemAmount || subscriptionDiscount.percentOff === 100) {
                        orderItem.isFullDiscount = true;
                    }
                }
                if (subscriptionTrialDaysDiscount.trialDays !== undefined) {
                    orderItem.discountId = subscriptionTrialDaysDiscount.id;
                }

                isFirstSubscriptionDiscountApplied = true;

                const trialDays = this.getTrialDays(subscriptionTrialDaysDiscount);

                if (trialDays != null) {
                    orderItem.trialDays = trialDays;
                }

                const billingAnchorDate = this.getBillingAnchorTimestamp();

                if (billingAnchorDate != null) {
                    orderItem.billingCycleAnchor = billingAnchorDate;
                }
            }

            if (product.type === ProductType.DONATION) {
                orderItem.recoveredProcessingFee = product.recoveredProcessingFee;
            }

            if (product.type === ProductType.NO_PAYMENT) {
                orderItem.amount = 0;
            }

            orderItems.push(orderItem);
        }

        return orderItems;
    }

    getTrialDays = (subscriptionTrialDaysDiscount: any) => {
        if (this.config.settings.trialDays !== undefined) {
            return this.config.settings.trialDays;
        }

        if (this.config.settings.billingDate) {
            return this.calculateTrialDays(this.config.settings.billingDate);
        }

        if (subscriptionTrialDaysDiscount && subscriptionTrialDaysDiscount.trialDays) {
            return subscriptionTrialDaysDiscount.trialDays;
        }

        return null;
    }

    getBillingAnchorTimestamp = () => {
        if (this.config.settings.billingAnchorDate) {
            return this.calculateAnchorTimestamp(this.config.settings.billingAnchorDate);
        }
        return null;
    }

    async getFormsSubmissionData() {
        const order: Order = new Order();

        let ipAddress;
        let hutk;

        for (let i = 0; i < this.forms.length; i++) {
            const form: any = this.forms[i];

            order.selectedProducts = [];

            if ((form.type === FormTypes.Stripe || form.type === FormTypes.Stripe_Google_Pay ||
                form.type === FormTypes.Stripe_Apple_Pay) && this.noPayment) {
                form.active = false;
                order.paymentMethod = {
                    type: this.paymentMethod,
                    paymentMethodId: null,
                    accountId: this.config.forms.stripe.accountId ? this.config.forms.stripe.accountId : null
                };
            }

            if (form.active) {
                if (form.type === FormTypes.Hubspot) {
                    ipAddress = form['ipAddress'];
                    hutk = form.hutk;
                }
                if (form.type === FormTypes.Subscription) {
                    this.subscriptionPlanId = form.currentSubscription.subscriptionPlanId;
                }
                if (form.type === FormTypes.Discount) {
                    this.discountId = form.coupon.id;
                    this.discountType = form.discountType;
                }

                await form.submit(order);

                if (order.selectedProducts.length > 0) {
                    order.orderItems = order.orderItems.concat(order.selectedProducts);
                }

                if (form.dynamicPlanInterval) {
                    if (form.view.getSubscriptionPlan() === DonationFrequencyType.ONE_TIME) {
                        this.dynamicPlanInterval = undefined;
                    }
                }
                if (form.type === FormTypes.DonationForm) {
                    this.dynamicPlanInterval = form.view.getSubscriptionPlan();
                    if (form.view.getSubscriptionPlan() === DonationFrequencyType.ONE_TIME) {
                        this.dynamicPlanInterval = undefined;
                    }
                }
            }
        }

        order.dfFormId = this.config.formId;
        order.mode = this.testMode;
        order.url = this.view.getCurrentURL();

        const getContextData = this.view.getContext();

        let hscontext: any = {
            ipAddress: ipAddress
        };

        if (getContextData) {
            hscontext.pageUrl = getContextData.pageUrl;
            hscontext.pageName = getContextData.pageTitle;
            hscontext.pageId = getContextData.pageId;
        }

        if (this.config.disableCookieSubmission) {
            hscontext.hutk = '';
        } else if (hutk) {
            hscontext.hutk = hutk;
        }

        if (this.config.followUpEmailId) {
            hscontext.followUpEmailId = this.config.followUpEmailId;
        }

        if (this.config.followUpEmailCampaignId) {
            hscontext.followUpEmailCampaignId = this.config.followUpEmailCampaignId;
        }

        if (this.config.customDealProperties) {
            order.customDealProperties = this.config.customDealProperties;
        }

        if (this.config.dealId) {
            order.hubSpotDealId = this.config.dealId;
        }

        hscontext = JSON.stringify(hscontext);
        order.hscontext = hscontext;

        if (order.orderItems) {
            const discountForm = this.getDiscountForm();

            order.orderItems = this.createOrderItems(order.orderItems,
                (discountForm || { discounts: [] }).discounts);
        }

        order.selectedProducts = [];
        order.redirect = this.getRedirectUrl();
        let stopSubmit: any;
        stopSubmit = this.triggerAppEvent('onFormSubmit', order);
        if (stopSubmit.formCallbackResult === false || stopSubmit.inlineCallbackResult === false) {
            throw new Error(`${Translations.FORM_SUBMIT_PREVENTED}`);
        }
        return order;
    }

    setNoPayment(submissionData: any) {
        const noPayment = submissionData.orderItems.every((item: any) => {
            return (parseInt(item.amount, 10) === 0);
        });
        if (noPayment) {
            submissionData.paymentType = ProductType.NO_PAYMENT;
        }
        return submissionData;
    }

    async submit() {
        const valid = await this.validate();
        this.view.startProgress();
        if (valid) {
            this.view.renderGlobalError(false);
            try {
                const submissionData = await this.getFormsSubmissionData();
                const data: any = Object.assign({
                    files: []
                }, submissionData);

                const config = {
                    host: process.env.API_URL,
                    portalId: this.config.user.portalId
                };

                delete data.subscriptionPlan;
                delete data.stripeEmail;
                Object.assign(this.data, data);

                await this.submitPayment(config);
            } catch (error: any) {
                this.logger.log(error);
                this.view.renderGlobalError(true);
                this.view.stopProgress();
                this.view.showError(error.message || error.errorMessage || error || Translations.SERVER_NOT_AVAILABLE, this.getErrorContainer());
            }
        } else {
            this.view.renderGlobalError(true);
            this.view.stopProgress();
        }
    }

    async submitPayment(config: any) {
        this.refreshRecaptchaToken();
        await this.provider.submitWidget(config, this.data, this.paymentMethod)
            .then(async (response) => {
                if (response.recaptcha.requiresAction) {
                    await this.handleRecaptcha(response, this.submit.bind(this));
                    return;
                }
                if (response.stripe.requiresAction) {
                    await this.handleCardAction(config, response);
                    return;
                }

                if (response.stripe.requiresBacsAction) {
                    await this.showStripeBacsMandateConfirmation(config, response);
                    return;
                }

                Object.assign(this.data, { result: response });
                const activeForms = this.getActiveForms();

                if (response.upsell && response.upsell.products && response.upsell.products.length) {
                    return activeForms.forEach(async (form: Form) => {
                        await form.upSellSubmit(this.data);
                    });
                }

                if (this.totalAmount === 0) {
                    this.postSubmitAnalyticsForms();
                    this.triggerAppEvent('onFormSubmitted', this.data);
                    this.redirectOrMessage();
                    return;
                }

                activeForms.forEach(async (form: Form) => {
                    await form.postSubmit(this.data);
                });
            })
            .catch((error: any) => {
                this.logger.log(error);
                this.view.stopProgress(this.paymentMethod);
                this.view.showError(error.errorMessage || error || Translations.SERVER_NOT_AVAILABLE, this.getErrorContainer());
            });
    }

    refreshRecaptchaToken = () => {
        const recaptchaV3Form = this.getRecaptchaV3Form();
        if (recaptchaV3Form) {
            recaptchaV3Form.fetchToken();
        }
    }

    handleRecaptcha = async (response: any, submitCallback: any) => {
        const recaptchaV3Form = this.getRecaptchaV3Form();
        const recaptchaV2Form = this.getRecaptchaV2Form();
        if (recaptchaV3Form && recaptchaV2Form) {
            this.closeStripeMobilePayModal();
            this.data.recaptchaSessionId = response.recaptcha.sessionId;
            this.data.recaptchaScore = response.recaptcha.score;
            const grecaptcha = recaptchaV3Form.getGrecaptchaInstance();
            await recaptchaV2Form.renderCheckbox(grecaptcha, response.recaptcha.errorMessage, this.data, submitCallback);
            this.view.stopProgress();
        }
    }

    handleCardAction = async (config: any, response: any) => {
        this.closeStripeMobilePayModal();

        const cardActionResult: any = await this.getStripeForm().handleCardAction(
            response.stripe.paymentIntentClientSecret,
            response.stripe.setupIntentClientSecret,
            this.getErrorContainer()
        );
        if (!cardActionResult.isCardAuthenticated && cardActionResult.error) {
            this.view.stopProgress();
            return;
        }
        this.data.orderId = response.orderId;
        this.data.paymentMethod.paymentIntentId = cardActionResult.paymentIntentId;
        this.data.paymentMethod.setupIntentId = cardActionResult.setupIntentId;

        const recaptchaV3Form = this.getRecaptchaV3Form();
        if (recaptchaV3Form) {
            this.data.recaptchaTokenV3 = recaptchaV3Form.getTokenV3();
        }

        this.submitPayment(config);
    }

    closeStripeMobilePayModal = () => {
        const stripeMobilePayForm = this.getStripeMobilePayForm();
        if (stripeMobilePayForm) {
            stripeMobilePayForm.closePaymentRequestModal();
        }
    }

    showStripeBacsMandateConfirmation = async (config: any, response: any) => {
        this.data.orderId = response.orderId;
        this.stripeBacsForm().showMandateConfirmation(config, response);
    }

    getActiveForms() {
        return this.forms.filter((form: Form) => form.active);
    }

    postSubmitAnalyticsForms() {
        const analyticsForms = this.forms.filter(
            (form: Form) => form.active && (form.type === FormTypes.FacebookPixel || form.type === FormTypes.GoogleAnalytics)
        );
        analyticsForms.forEach(async (form: Form) => {
            await form.postSubmit(this.data);
        });
    }

    getAmount() {
        let dataAmount = this.totalAmount;
        if (this.config.externalAmount) {
            dataAmount = this.config.externalAmount;
        }
        return dataAmount;
    }

    getErrorContainer() {
        return this.errorView.getErrorContainer(this.paymentMethod);
    }

    getSubscriptionForm() {
        return <SubscriptionForm>this.forms.find(form => form.type === FormTypes.Subscription);
    }

    getProductsForm() {
        return <ProductForm>this.forms.find(form => form.type === FormTypes.Product);
    }

    getAdaptiveProductsForm() {
        return <ProductForm>this.forms.find(form => form.type === FormTypes.AdaptivePrices);
    }

    getAdaptiveSubscriptionForm() {
        return <ProductForm>this.forms.find(form => form.type === FormTypes.AdaptiveSubscriptionPrices);
    }

    getDonationForm() {
        return <DonationForm>this.forms.find(form => form.type === FormTypes.DonationForm);
    }

    getDiscountForm() {
        return <DiscountForm>this.forms.find(form => form.type === FormTypes.Discount);
    }

    getStripeForm() {
        return <StripeForm>this.forms.find(form => form.type === FormTypes.Stripe);
    }

    getPayPalForm() {
        return <PaypalForm>this.forms.find(form => form.type === FormTypes.Paypal);
    }

    getStripeMobilePayForm(): StripeMobilePayForm {
        return this.getStripeGooglePayForm() || this.getStripeApplePayForm();
    }

    getStripeGooglePayForm() {
        return <StripeGooglePayForm>this.forms.find(form => form.type === FormTypes.Stripe_Google_Pay);
    }

    getStripeApplePayForm() {
        return <StripeApplePayForm>this.forms.find(form => form.type === FormTypes.Stripe_Apple_Pay);
    }

    getHubSpotForm() {
        return <HubSpotForm>this.forms.find(form => form.type === FormTypes.Hubspot);
    }

    getMultiContactsForm() {
        return <MultipleContactsForm>this.forms.find(form => form.type === FormTypes.MultipleContacts);
    }

    getLogFailingOnInitForm() {
        return <LogFailingOnInitForm>this.forms.find(form => form.type === FormTypes.LogFailingOnInit);
    }

    getLogFailingOnRenderForm() {
        return <LogFailingOnRenderForm>this.forms.find(form => form.type === FormTypes.LogFailingOnRender);
    }

    getAddonForm() {
        return <AddonForm>this.forms.find(form => form.type === FormTypes.Addon);
    }

    getRecaptchaV3Form() {
        return <RecaptchaV3Form>this.forms.find(form => form.type === FormTypes.RecaptchaV3);
    }

    getRecaptchaV2Form() {
        return <RecaptchaV2Form>this.forms.find(form => form.type === FormTypes.RecaptchaV2);
    }

    getOfflinePaymentForm() {
        return <OfflinePaymentForm>this.forms.find(form => form.type === FormTypes.OfflinePayment);
    }

    getStripeAchForm(): StripeAchForm {
        return <StripeAchForm>this.forms.find(form => form.type === FormTypes.Stripe_Ach);
    }

    getCountdownForm(): CountdownForm {
        return <CountdownForm>this.forms.find(form => form.type === FormTypes.CountdownTimer);
    }

    stripeBacsForm(): StripeBacsForm {
        return <StripeBacsForm>this.forms.find(form => form.type === FormTypes.Stripe_Bacs);
    }

    calculateTrialDays(date: string) {

        const bilingDate = Date.parse(date + ' 00:00');
        const today = new Date().getTime();

        return Math.max(0,
            Math.ceil(
                (bilingDate - today) / (60 * 60 * 24 * 1000)));
    }

    calculateAnchorTimestamp(anchorDate: number) {

        const now = new Date();
        const currentDay = now.getDate();

        now.setDate(anchorDate);
        now.setHours(2, 0, 0);
        now.setDate(anchorDate);

        if (currentDay >= anchorDate) {
            now.setMonth(now.getMonth() + 1);
        }

        return new Date(now).getTime() / 1000;
    }

    getSettings() {
        return this.config.settings;
    }

    clearContainer() {
        return this.view.clearContainer();
    }

    checkForRedirects() {
        this.hsThankYouMessage = this.getThankYouMessage();
        this.hsInputRedirectUrl = this.getRedirectUrl();
    }

    showUpSellThankMessage() {
        if (this.hsThankYouMessage && this.hsThankYouMessage.length) {
            this.view.renderThankYouMessage(this.hsThankYouMessage);
        } else {
            this.redirectToUrl(this.hsInputRedirectUrl);
        }
    }

    renderUpSell() {
        this.checkForRedirects();
        const upsellProducts = this.config.widget.data.result.upsell.products as Product[];
        this.forms = [];

        if (upsellProducts.length) {
            if (upsellProducts.some(x => x.type === 'single_payment')) {
                this.forms.push(FormsFactory.createForm(FormTypes.UpSellProduct));
            }

            if (upsellProducts.some(x => x.type === 'subscription')) {
                this.forms.push(FormsFactory.createForm(FormTypes.UpSellSubscription));
            }

            if (upsellProducts.some(x => x.type === 'donation')) {
                this.forms.push(FormsFactory.createForm(FormTypes.UpSellDonation));
            }
        }

        this.forms.push(FormsFactory.createForm(FormTypes.UpSell));

        if (this.config.recaptchaScoreEnabled && this.config.recaptchaScoreEnabled === true) {
            this.forms.push(FormsFactory.createForm(FormTypes.RecaptchaV3));
        }
        if (this.config.recaptchaCheckboxEnabled && this.config.recaptchaCheckboxEnabled === true) {
            this.forms.push(FormsFactory.createForm(FormTypes.RecaptchaV2));
        }

        const upSellFormInstance = this.forms.find(x => x.type === FormTypes.UpSell) as UpSellForm;

        if (upSellFormInstance) {
            const boundSubject = new Subject<IUpSellEvent>();
            return Promise.all(
                this.forms.map(form => {
                    const formInitPromise = form.init(
                        this.config,
                        form.type === FormTypes.UpSell ? this.initCallbacks : upSellFormInstance.upSellCallbacks,
                        boundSubject
                    );
                    if (this.isLogging) {
                        formInitPromise.catch(err => {
                            err.message = `An error occured while initializing from - ${form.type} -: ` + err.stack;
                            this.provider.logError(err, { formId: this.config.formId, portalId: this.config.user.portalId });
                        });
                    }
                })).then(res => {
                    this.view.stopProgress();
                    this.clearContainer();

                    this.forms.forEach(form => form.render().catch((err: Error) => {
                        err.message = 'Form ' + form.type + ' had an error while rendering: ' + err.message;
                        this.provider.logError(err, { formId: this.config.formId, portalId: this.config.user.portalId });
                    }));

                    this.view.renderCopyright(this.view.getHtmlElement('#df-card-container'));
                    this.renderTestBanner();
                });
        }
    }

}
